/**
 * "New state" on a diagram paired with a machine (decision 030).
 *
 * Makes a state DEF - beside the machine, at package level, where the States
 * page lists defs - and a usage of it in the machine (or in the selected box),
 * in one request and one commit. The def states the facts; the usage is the
 * machine's, so this is the only box the diagram gets.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { StateUsageCreated } from '../../schema'
import { MONOSPACE } from '../../theme'

/** What a new def specializes: a plain state, a commanded mode, or a mission phase. */
const SORTS = [
  { type: 'EchelonState', label: 'State' },
  { type: 'EchelonMode', label: 'Mode' },
  { type: 'EchelonPhase', label: 'Phase' },
] as const

export default function NewUsageDialog({
  machine,
  label,
  onClose,
  onCreated,
}: {
  /** The qualified name of the machine, or the composite state, it goes inside. */
  machine: string
  /** What the machine is called, for the title. */
  label: string
  onClose: () => void
  onCreated: (made: StateUsageCreated) => void
}) {
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [sort, setSort] = useState<string>('EchelonState')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const submit = () => {
    setBusy(true)
    setFailure('')
    api
      .createStateUsage({
        machine,
        new_definition: { identifier, ...(name ? { name } : {}), type_name: sort },
      })
      .then(onCreated)
      .catch((error: ApiError) => setFailure(error.message))
      .finally(() => setBusy(false))
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New state in {label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Makes a state def beside the machine, and puts a usage of it in{' '}
            <span style={{ fontFamily: MONOSPACE }}>{label}</span>. Other machines can use the same def.
          </Typography>
          <TextField
            label="ID"
            required
            size="small"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            helperText="The def's short name, e.g. ST_SAFE"
            slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
          />
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            helperText="What transitions name it by; defaults to the ID"
          />
          <RadioGroup row value={sort} onChange={(event) => setSort(event.target.value)}>
            {SORTS.map((entry) => (
              <FormControlLabel key={entry.type} value={entry.type} control={<Radio size="small" />} label={entry.label} />
            ))}
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!identifier || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
